import { z } from 'zod';

import { BooleanQueryParamSchema } from './common.js';
import { CatalogGeoFacetsSchema } from './geo.js';
import { RoomGroupInputSchema } from './itineraries.js';
import { paginatedResponseSchema } from './pagination.js';

/**
 * Fixed Departures - a standalone TEMPLATE master (not a booked trip, not a
 * dated departure).
 *
 * It captures trip STRUCTURE (cities + nights, day-by-day) plus OPTION POOLS
 * (every hotel / flight / activity that could be used). Real travel dates and
 * the final picks happen later, when an Itinerary is created FROM the template.
 *
 * Conventions mirrored from `packages.ts`:
 *   - `<X>OutSchema`            : API response shape
 *   - `Create<X>RequestSchema`  : create payload
 *   - `Update<X>RequestSchema`  : the create schema `.partial()`
 *
 * Money is a stringified BigInt of PAISE (same as itinerary / quote / booking),
 * NOT the Decimal-rupees convention used by `packages`.
 */

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/** Catalog approval lifecycle - shared with Activity / Transfer / Hotel / etc. */
export const FixedDepartureStatusSchema = z.enum([
  'DRAFT',
  'PENDING_APPROVAL',
  'ACTIVE',
  'INACTIVE',
  'ARCHIVED',
]);
export type FixedDepartureStatus = z.infer<typeof FixedDepartureStatusSchema>;

export const FixedDepartureMarkupTypeSchema = z.enum(['FIXED', 'PERCENTAGE']);
export type FixedDepartureMarkupType = z.infer<typeof FixedDepartureMarkupTypeSchema>;

/** Which axis of the vendor price grid a cell sits on. */
export const FixedDeparturePaxSlabKindSchema = z.enum([
  'ADULT_SLAB',
  'EXTRA_BED',
  'CHILD_WITH_BED',
  'CHILD_WITHOUT_BED',
  'INFANT',
  'SINGLE_SUPPLEMENT',
]);
export type FixedDeparturePaxSlabKind = z.infer<typeof FixedDeparturePaxSlabKindSchema>;

export const FixedDepartureSupplementBasisSchema = z.enum(['PER_TOTAL', 'PER_PERSON']);
export type FixedDepartureSupplementBasis = z.infer<typeof FixedDepartureSupplementBasisSchema>;

/**
 * Kind of FD supplement. VEHICLE keeps the original transport behaviour (a
 * TRANSFER component keyed to a vehicle type); VISA / INSURANCE / OTHER become
 * dayless VAS add-ons - a priced inclusion line that never enters the day plan.
 */
export const FixedDepartureSupplementKindSchema = z.enum(['VEHICLE', 'VISA', 'INSURANCE', 'OTHER']);
export type FixedDepartureSupplementKind = z.infer<typeof FixedDepartureSupplementKindSchema>;

/** Paise as a decimal string (BigInt-safe over JSON). */
const paiseString = z.string().regex(/^-?\d+$/, 'must be an integer paise string');
/** "HH:mm" free-text time, matching the legacy flight time slots. */
const timeString = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be HH:mm')
  .nullable();

// ---------------------------------------------------------------------------
// Children - OUT
// ---------------------------------------------------------------------------

export const FixedDepartureDepartureDateOutSchema = z.object({
  id: z.string(),
  departureDate: z.iso.date(),
  surchargePaise: z.string().regex(/^-?\d+$/),
  isActive: z.boolean(),
});
export type FixedDepartureDepartureDateOut = z.infer<typeof FixedDepartureDepartureDateOutSchema>;

/**
 * One blockout range: days the FD does NOT operate. Inclusive on both ends; a
 * single blocked day has `from === to`. `from` must be on or before `to` (the
 * API and admin both enforce this). A trip whose travel dates overlap any range
 * cannot be created from the FD.
 */
export const FixedDepartureBlockoutRangeSchema = z
  .object({
    from: z.iso.date(),
    to: z.iso.date(),
  })
  .refine((r) => r.from <= r.to, { message: 'Blockout "from" must be on or before "to"' });
export type FixedDepartureBlockoutRange = z.infer<typeof FixedDepartureBlockoutRangeSchema>;

export const FixedDepartureCityOutSchema = z.object({
  id: z.string(),
  sortOrder: z.number().int(),
  cityId: z.string(),
  cityName: z.string().nullable(),
  nights: z.number().int(),
});
export type FixedDepartureCityOut = z.infer<typeof FixedDepartureCityOutSchema>;

export const FixedDepartureDayOutSchema = z.object({
  id: z.string(),
  dayIndex: z.number().int(),
  cityId: z.string().nullable(),
  cityName: z.string().nullable(),
  title: z.string().nullable(),
  subtitle: z.string().nullable(),
  mealPlanId: z.string().nullable(),
  mealPlanCode: z.string().nullable(),
  mealPlanName: z.string().nullable(),
  briefHtml: z.string().nullable(),
  heroImageUrl: z.string().nullable(),
  galleryImageUrls: z.array(z.string()),
});
export type FixedDepartureDayOut = z.infer<typeof FixedDepartureDayOutSchema>;

/** One cell of the vendor price grid. */
export const FixedDepartureCategoryPriceOutSchema = z.object({
  id: z.string(),
  kind: FixedDeparturePaxSlabKindSchema,
  /**
   * Inclusive group-size band for ADULT_SLAB rows. An exact size is `from ==
   * to` ("02 Adults" -> 2..2); a range is "2-3 PAX" -> 2..3. Both ends sit at
   * 0 on the per-head kinds.
   */
  paxFrom: z.number().int(),
  paxTo: z.number().int(),
  pricePaise: paiseString,
});
export type FixedDepartureCategoryPriceOut = z.infer<typeof FixedDepartureCategoryPriceOutSchema>;

/** A grade tier: its hotel set lives on `hotels`, its price column on `prices`. */
export const FixedDepartureCategoryOutSchema = z.object({
  id: z.string(),
  sortOrder: z.number().int(),
  name: z.string(),
  starLabel: z.string().nullable(),
  prices: z.array(FixedDepartureCategoryPriceOutSchema),
});
export type FixedDepartureCategoryOut = z.infer<typeof FixedDepartureCategoryOutSchema>;

export const FixedDepartureHotelOutSchema = z.object({
  id: z.string(),
  sortOrder: z.number().int(),
  /** Every hotel belongs to a tier - that is where its price column lives. */
  categoryId: z.string(),
  categoryName: z.string().nullable(),
  cityId: z.string(),
  cityName: z.string().nullable(),
  hotelName: z.string(),
  nights: z.number().int(),
  occupancyTypeId: z.string().nullable(),
  occupancyTypeName: z.string().nullable(),
});
export type FixedDepartureHotelOut = z.infer<typeof FixedDepartureHotelOutSchema>;

export const FixedDepartureVehicleSupplementOutSchema = z.object({
  id: z.string(),
  sortOrder: z.number().int(),
  kind: FixedDepartureSupplementKindSchema,
  vehicleTypeId: z.string().nullable(),
  vehicleTypeName: z.string().nullable(),
  label: z.string().nullable(),
  condition: z.string().nullable(),
  /** PRIVATE (PER_TOTAL): cost of one vehicle. Also the SIC per-adult fallback. */
  amountPaise: paiseString,
  /** SIC (PER_PERSON) per-adult rate; falls back to amountPaise when 0. */
  adultPricePaise: paiseString,
  /** SIC (PER_PERSON) per-child rate. */
  childPricePaise: paiseString,
  basis: FixedDepartureSupplementBasisSchema,
});
export type FixedDepartureVehicleSupplementOut = z.infer<
  typeof FixedDepartureVehicleSupplementOutSchema
>;

/** Markup + rate validity. The prices themselves live on the category grid. */
export const FixedDepartureLandPackageOutSchema = z.object({
  id: z.string(),
  markupType: FixedDepartureMarkupTypeSchema,
  markupPaise: paiseString.nullable(),
  markupPercent: z.string().nullable(),
  validFrom: z.iso.date().nullable(),
  validTo: z.iso.date().nullable(),
});
export type FixedDepartureLandPackageOut = z.infer<typeof FixedDepartureLandPackageOutSchema>;

export const FixedDepartureFlightOutSchema = z.object({
  id: z.string(),
  sortOrder: z.number().int(),
  fromCityId: z.string(),
  fromCityName: z.string().nullable(),
  toCityId: z.string(),
  toCityName: z.string().nullable(),
  /** Airport chosen at each end when the city has neighbouring airports; null = the city's own code. */
  fromAirportCode: z.string().nullable().default(null),
  toAirportCode: z.string().nullable().default(null),
  airlineId: z.string().nullable(),
  airlineName: z.string().nullable(),
  flightNumber: z.string().nullable(),
  departureDate: z.iso.date().nullable(),
  departureTime: z.string().nullable(),
  arrivalDate: z.iso.date().nullable(),
  arrivalTime: z.string().nullable(),
  /**
   * Block flights are an OPTIONAL, per-pax-priced add-on (ADR-0069). These are
   * the held-inventory fares by age band: Adult 12+, Child 2-12, Infant 0-2.
   */
  adultPricePaise: paiseString,
  childPricePaise: paiseString,
  infantPricePaise: paiseString,
});
export type FixedDepartureFlightOut = z.infer<typeof FixedDepartureFlightOutSchema>;

export const FixedDepartureInclusionOutSchema = z.object({
  id: z.string(),
  sortOrder: z.number().int(),
  text: z.string(),
});
export type FixedDepartureInclusionOut = z.infer<typeof FixedDepartureInclusionOutSchema>;

export const FixedDepartureExclusionOutSchema = FixedDepartureInclusionOutSchema;
export type FixedDepartureExclusionOut = z.infer<typeof FixedDepartureExclusionOutSchema>;

export const FixedDepartureActivityOutSchema = z.object({
  id: z.string(),
  sortOrder: z.number().int(),
  tourName: z.string(),
  adultPricePaise: paiseString,
  childPricePaise: paiseString,
  infantPricePaise: paiseString,
  currencyId: z.string().nullable(),
  currencyCode: z.string().nullable(),
});
export type FixedDepartureActivityOut = z.infer<typeof FixedDepartureActivityOutSchema>;

// ---------------------------------------------------------------------------
// Parent - OUT
// ---------------------------------------------------------------------------

/** Row shape for the admin list grid (no children - keeps the list query cheap). */
export const FixedDepartureListItemSchema = z.object({
  id: z.string(),
  reference: z.string(),
  title: z.string(),
  status: FixedDepartureStatusSchema,
  numberOfNights: z.number().int(),
  numberOfDays: z.number().int(),
  /** "Srinagar, Gulmarg" - joined city names, for the grid's Destination column. */
  citySummary: z.string().nullable(),
  /** "Standard, Deluxe, Premium" - the tier ladder, so agents can compare rows. */
  categorySummary: z.string().nullable(),
  /** Cheapest cell across the whole grid, for the picker's "from" price. */
  startingPricePaise: paiseString.nullable(),
  currencyCode: z.string().nullable(),
  /**
   * Day images across the whole template (hero + gallery), for the list's
   * Readiness chip. A template with none has nothing to put on the docket.
   */
  imageCount: z.number().int().default(0),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FixedDepartureListItem = z.infer<typeof FixedDepartureListItemSchema>;

export const FixedDepartureListResponseSchema = paginatedResponseSchema(
  FixedDepartureListItemSchema,
);
export type FixedDepartureListResponse = z.infer<typeof FixedDepartureListResponseSchema>;

/**
 * In-use countries / cities for the Fixed departures list filters - see
 * {@link CatalogGeoFacetsSchema}. A template has an ordered city LIST rather
 * than one city, so a city appears here if any template stops there, and the
 * `cityId` list filter matches templates that include it.
 */
export const FixedDepartureFacetsSchema = CatalogGeoFacetsSchema;
export type FixedDepartureFacets = z.infer<typeof FixedDepartureFacetsSchema>;

export const FixedDepartureDetailSchema = FixedDepartureListItemSchema.extend({
  supplierId: z.string().nullable(),
  supplierName: z.string().nullable(),
  /** Room basis: an FK, because rooms = ceil(adults / paxCount) depends on it. */
  sharingOccupancyTypeId: z.string().nullable(),
  sharingOccupancyName: z.string().nullable(),
  sharingPaxCount: z.number().int().nullable(),
  currencyId: z.string().nullable(),
  currencyCode: z.string().nullable(),
  notes: z.string().nullable(),
  blockoutDates: z.array(FixedDepartureBlockoutRangeSchema),
  cities: z.array(FixedDepartureCityOutSchema),
  departureDates: z.array(FixedDepartureDepartureDateOutSchema),
  days: z.array(FixedDepartureDayOutSchema),
  categories: z.array(FixedDepartureCategoryOutSchema),
  hotels: z.array(FixedDepartureHotelOutSchema),
  flights: z.array(FixedDepartureFlightOutSchema),
  landPackage: FixedDepartureLandPackageOutSchema.nullable(),
  vehicleSupplements: z.array(FixedDepartureVehicleSupplementOutSchema),
  inclusions: z.array(FixedDepartureInclusionOutSchema),
  exclusions: z.array(FixedDepartureExclusionOutSchema),
  activities: z.array(FixedDepartureActivityOutSchema),
});
export type FixedDepartureDetail = z.infer<typeof FixedDepartureDetailSchema>;

// ---------------------------------------------------------------------------
// Parent - requests
// ---------------------------------------------------------------------------

export const CreateFixedDepartureRequestSchema = z.object({
  title: z.string().min(1, 'Title is required').max(300),
  status: FixedDepartureStatusSchema.optional(),
  supplierId: z.string().uuid().nullable().optional(),
  sharingOccupancyTypeId: z.string().uuid().nullable().optional(),
  currencyId: z.string().uuid().nullable().optional(),
  notes: z.string().max(8000).nullable().optional(),
  blockoutDates: z.array(FixedDepartureBlockoutRangeSchema).max(100).optional(),
});
export type CreateFixedDepartureRequest = z.infer<typeof CreateFixedDepartureRequestSchema>;

export const UpdateFixedDepartureRequestSchema = CreateFixedDepartureRequestSchema.partial();
export type UpdateFixedDepartureRequest = z.infer<typeof UpdateFixedDepartureRequestSchema>;

/**
 * Whole-config save for the builder's Configure step: replaces the city list
 * and re-syncs day rows (add / remove) to match nights + 1, preserving the
 * text already typed into surviving days.
 */
export const SaveFixedDepartureCitiesRequestSchema = z.object({
  cities: z
    .array(
      z.object({
        cityId: z.string().uuid(),
        nights: z.number().int().min(1).max(60),
      }),
    )
    .min(1, 'Add at least one city'),
});
export type SaveFixedDepartureCitiesRequest = z.infer<typeof SaveFixedDepartureCitiesRequestSchema>;

export const FixedDepartureListQuerySchema = z.object({
  q: z.string().optional(),
  status: FixedDepartureStatusSchema.optional(),
  /** Picker filters (legacy parity). Both match templates that STOP there -
   *  a template has a city list, not one city. */
  cityId: z.string().uuid().optional(),
  countryId: z.string().uuid().optional(),
  nights: z.coerce.number().int().min(0).max(60).optional(),
  /** Open-ended bucket for the picker's "9+ nights" option. */
  minNights: z.coerce.number().int().min(0).max(60).optional(),
  categoryName: z.string().optional(),
  /**
   * Agent-facing pickers must only ever see sellable templates. Defaults to
   * false; the admin list screen passes true to include the rest.
   */
  sellableOnly: z.coerce.boolean().optional(),
  /**
   * Readiness filter, images half. `true` = at least one day image anywhere in
   * the template; `false` = none at all.
   */
  hasImages: BooleanQueryParamSchema.optional(),
  /**
   * Readiness filter, fields half. `true` = rows the Readiness chip badges as
   * "N missing" (no starting price, no destination, or no tier); `false` = rows
   * badged "Info complete". Images are the other, independent half - filter
   * those with `hasImages`; the two combine.
   */
  missingInfo: BooleanQueryParamSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  sortBy: z.enum(['createdAt', 'updatedAt', 'title', 'reference']).default('createdAt'),
  sortDir: z.enum(['asc', 'desc']).default('desc'),
});
export type FixedDepartureListQuery = z.infer<typeof FixedDepartureListQuerySchema>;

// ---------------------------------------------------------------------------
// Children - requests
// ---------------------------------------------------------------------------

export const UpdateFixedDepartureDayRequestSchema = z.object({
  title: z.string().max(300).nullable().optional(),
  subtitle: z.string().max(300).nullable().optional(),
  mealPlanId: z.string().uuid().nullable().optional(),
  briefHtml: z.string().nullable().optional(),
  heroImageUrl: z.string().max(2000).nullable().optional(),
  /** Capped at 10 - a day gallery, not a media library. */
  galleryImageUrls: z.array(z.string().max(2000)).max(10).optional(),
});
export type UpdateFixedDepartureDayRequest = z.infer<typeof UpdateFixedDepartureDayRequestSchema>;

export const CreateFixedDepartureCategoryRequestSchema = z.object({
  name: z.string().min(1, 'Tier name is required').max(120),
  starLabel: z.string().max(120).nullable().optional(),
});
export type CreateFixedDepartureCategoryRequest = z.infer<
  typeof CreateFixedDepartureCategoryRequestSchema
>;
export const UpdateFixedDepartureCategoryRequestSchema =
  CreateFixedDepartureCategoryRequestSchema.partial();
export type UpdateFixedDepartureCategoryRequest = z.infer<
  typeof UpdateFixedDepartureCategoryRequestSchema
>;

/**
 * Whole-column save for one tier's prices. Replaces every cell on the
 * category, so the grid editor can post what it has without diffing.
 */
export const SaveFixedDepartureCategoryPricesRequestSchema = z.object({
  prices: z
    .array(
      z
        .object({
          kind: FixedDeparturePaxSlabKindSchema,
          paxFrom: z.number().int().min(0).max(99).optional(),
          paxTo: z.number().int().min(0).max(99).optional(),
          pricePaise: paiseString,
        })
        .refine((p) => (p.paxTo ?? 0) >= (p.paxFrom ?? 0), {
          message: 'Band end must be at or after its start',
          path: ['paxTo'],
        }),
    )
    .max(80),
});
export type SaveFixedDepartureCategoryPricesRequest = z.infer<
  typeof SaveFixedDepartureCategoryPricesRequestSchema
>;

export const CreateFixedDepartureVehicleSupplementRequestSchema = z
  .object({
    kind: FixedDepartureSupplementKindSchema.optional(),
    vehicleTypeId: z.string().uuid().nullable().optional(),
    label: z.string().max(200).nullable().optional(),
    condition: z.string().max(300).nullable().optional(),
    amountPaise: paiseString.optional(),
    adultPricePaise: paiseString.optional(),
    childPricePaise: paiseString.optional(),
    basis: FixedDepartureSupplementBasisSchema.optional(),
  })
  .refine(
    (v) =>
      // VEHICLE may identify itself by a master vehicle type OR a free label;
      // every other kind (visa/insurance/other) is label-only.
      (v.kind ?? 'VEHICLE') === 'VEHICLE'
        ? Boolean(v.vehicleTypeId) || Boolean(v.label?.trim())
        : Boolean(v.label?.trim()),
    {
      message: 'Pick a vehicle type or type a label',
      path: ['label'],
    },
  );
export type CreateFixedDepartureVehicleSupplementRequest = z.infer<
  typeof CreateFixedDepartureVehicleSupplementRequestSchema
>;
/** Partial of the create SHAPE - `.partial()` is unavailable on a refined schema. */
export const UpdateFixedDepartureVehicleSupplementRequestSchema = z.object({
  kind: FixedDepartureSupplementKindSchema.optional(),
  vehicleTypeId: z.string().uuid().nullable().optional(),
  label: z.string().max(200).nullable().optional(),
  condition: z.string().max(300).nullable().optional(),
  amountPaise: paiseString.optional(),
  adultPricePaise: paiseString.optional(),
  childPricePaise: paiseString.optional(),
  basis: FixedDepartureSupplementBasisSchema.optional(),
});
export type UpdateFixedDepartureVehicleSupplementRequest = z.infer<
  typeof UpdateFixedDepartureVehicleSupplementRequestSchema
>;

export const CreateFixedDepartureHotelRequestSchema = z.object({
  categoryId: z.string().uuid(),
  cityId: z.string().uuid(),
  // Free text (property list + "/ similar" escape hatch); the DB column is
  // unlimited `text`, so keep the cap generous - operators list several
  // properties per row and were hitting the old 300-char limit.
  hotelName: z.string().min(1).max(2000),
  nights: z.number().int().min(1).max(60).optional(),
  occupancyTypeId: z.string().uuid().nullable().optional(),
});
export type CreateFixedDepartureHotelRequest = z.infer<
  typeof CreateFixedDepartureHotelRequestSchema
>;
export const UpdateFixedDepartureHotelRequestSchema =
  CreateFixedDepartureHotelRequestSchema.partial();
export type UpdateFixedDepartureHotelRequest = z.infer<
  typeof UpdateFixedDepartureHotelRequestSchema
>;

export const SaveFixedDepartureLandPackageRequestSchema = z.object({
  markupType: FixedDepartureMarkupTypeSchema.optional(),
  markupPaise: paiseString.nullable().optional(),
  markupPercent: z.string().nullable().optional(),
  validFrom: z.iso.date().nullable().optional(),
  validTo: z.iso.date().nullable().optional(),
});
export type SaveFixedDepartureLandPackageRequest = z.infer<
  typeof SaveFixedDepartureLandPackageRequestSchema
>;

export const CreateFixedDepartureFlightRequestSchema = z.object({
  fromCityId: z.string().uuid(),
  toCityId: z.string().uuid(),
  /** Airport (IATA) at each end; null / omitted = the city's own code. */
  fromAirportCode: z
    .string()
    .trim()
    .regex(/^[A-Z]{3}$/)
    .nullable()
    .optional(),
  toAirportCode: z
    .string()
    .trim()
    .regex(/^[A-Z]{3}$/)
    .nullable()
    .optional(),
  airlineId: z.string().uuid().nullable().optional(),
  flightNumber: z.string().max(20).nullable().optional(),
  departureDate: z.iso.date().nullable().optional(),
  departureTime: timeString.optional(),
  arrivalDate: z.iso.date().nullable().optional(),
  arrivalTime: timeString.optional(),
  adultPricePaise: paiseString.optional(),
  childPricePaise: paiseString.optional(),
  infantPricePaise: paiseString.optional(),
});
export type CreateFixedDepartureFlightRequest = z.infer<
  typeof CreateFixedDepartureFlightRequestSchema
>;
export const UpdateFixedDepartureFlightRequestSchema =
  CreateFixedDepartureFlightRequestSchema.partial();
export type UpdateFixedDepartureFlightRequest = z.infer<
  typeof UpdateFixedDepartureFlightRequestSchema
>;

export const CreateFixedDepartureTextRequestSchema = z.object({
  text: z.string().min(1).max(1000),
});
export type CreateFixedDepartureTextRequest = z.infer<typeof CreateFixedDepartureTextRequestSchema>;
export const UpdateFixedDepartureTextRequestSchema =
  CreateFixedDepartureTextRequestSchema.partial();
export type UpdateFixedDepartureTextRequest = z.infer<typeof UpdateFixedDepartureTextRequestSchema>;

export const CreateFixedDepartureActivityRequestSchema = z.object({
  tourName: z.string().min(1).max(300),
  adultPricePaise: paiseString.optional(),
  childPricePaise: paiseString.optional(),
  infantPricePaise: paiseString.optional(),
  currencyId: z.string().uuid().nullable().optional(),
});
export type CreateFixedDepartureActivityRequest = z.infer<
  typeof CreateFixedDepartureActivityRequestSchema
>;
export const UpdateFixedDepartureActivityRequestSchema =
  CreateFixedDepartureActivityRequestSchema.partial();
export type UpdateFixedDepartureActivityRequest = z.infer<
  typeof UpdateFixedDepartureActivityRequestSchema
>;

/** Generic reorder payload for the option-pool lists (hotels / flights / text rows). */
export const ReorderFixedDepartureChildrenRequestSchema = z.object({
  ids: z.array(z.string().uuid()).min(1),
});
export type ReorderFixedDepartureChildrenRequest = z.infer<
  typeof ReorderFixedDepartureChildrenRequestSchema
>;

// ---------------------------------------------------------------------------
// Creating an itinerary FROM a fixed departure (ADR-0069)
// ---------------------------------------------------------------------------

/**
 * How the new itinerary should be seeded with flights.
 *   - `NONE`  : land package only. Flights can always be added later.
 *   - `LIVE`  : the builder opens the TBO search with a pre-filled config.
 *   - `BLOCK` : the template's own block flights are attached.
 */
export const FixedDepartureFlightModeSchema = z.enum(['NONE', 'LIVE', 'BLOCK']);
export type FixedDepartureFlightMode = z.infer<typeof FixedDepartureFlightModeSchema>;

export const CreateItineraryFromFixedDepartureRequestSchema = z.object({
  fixedDepartureId: z.string().uuid(),
  /**
   * Which grade tier is being sold - fixes both the hotel set and the price.
   * Optional in the pick-only create flow: the service defaults to the cheapest
   * tier and the agent refines it on the config page (like FIT).
   */
  categoryId: z.string().uuid().optional(),
  /** Day 1. Optional on pick-only create; the service seeds a sensible default. */
  travelStartDate: z.iso.date().optional(),
  flightMode: FixedDepartureFlightModeSchema.default('NONE'),
  /** Overrides the lead's pax counts when the agent adjusts them in the picker. */
  adults: z.number().int().min(1).max(99).optional(),
  childrenWithBed: z.number().int().min(0).max(99).optional(),
  childrenWithoutBed: z.number().int().min(0).max(99).optional(),
  infants: z.number().int().min(0).max(99).optional(),
  singleRooms: z.number().int().min(0).max(99).optional(),
  /**
   * Families > rooms. When present the land price is computed per room and the
   * flat pax fields above are ignored for pricing (they are derived from it).
   */
  roomGroups: z.array(RoomGroupInputSchema).min(1).max(20).optional(),
  /** Vehicle supplements the agent accepted, by id. */
  vehicleSupplementIds: z.array(z.string().uuid()).max(20).optional(),
  title: z.string().min(1).max(300).optional(),
});
export type CreateItineraryFromFixedDepartureRequest = z.infer<
  typeof CreateItineraryFromFixedDepartureRequestSchema
>;

/**
 * Dry-run of the land price for a given tier + party, so the picker can show
 * the real number before anything is created.
 */
export const FixedDepartureQuotePreviewSchema = z.object({
  categoryId: z.string(),
  categoryName: z.string(),
  currencyCode: z.string().nullable(),
  /** The matched adult band, e.g. "4-5 Pax". Null when nothing matched. */
  bandLabel: z.string().nullable(),
  lines: z.array(
    z.object({
      label: z.string(),
      quantity: z.number().int(),
      unitPricePaise: paiseString,
      totalPaise: paiseString,
    }),
  ),
  landTotalPaise: paiseString,
  /** Populated when the party cannot be priced or breaks the bed ceiling. */
  unpricedReason: z.string().nullable(),
  /** Rooms implied by the sharing basis, and the extra-bed ceiling it sets. */
  roomsRequired: z.number().int().nullable(),
  maxChildrenWithBed: z.number().int().nullable(),
  /** Per-room breakdown when priced from room configuration; [] otherwise. */
  rooms: z
    .array(
      z.object({
        groupIndex: z.number().int(),
        roomIndex: z.number().int(),
        lines: z.array(
          z.object({
            label: z.string(),
            quantity: z.number().int(),
            unitPricePaise: paiseString,
            totalPaise: paiseString,
          }),
        ),
        totalPaise: paiseString,
      }),
    )
    .default([]),
});
export type FixedDepartureQuotePreview = z.infer<typeof FixedDepartureQuotePreviewSchema>;

export const FixedDepartureQuotePreviewQuerySchema = z.object({
  categoryId: z.string().uuid(),
  adults: z.coerce.number().int().min(1).max(99),
  childrenWithBed: z.coerce.number().int().min(0).max(99).optional(),
  childrenWithoutBed: z.coerce.number().int().min(0).max(99).optional(),
  infants: z.coerce.number().int().min(0).max(99).optional(),
  singleRooms: z.coerce.number().int().min(0).max(99).optional(),
});
export type FixedDepartureQuotePreviewQuery = z.infer<typeof FixedDepartureQuotePreviewQuerySchema>;

/** POST body: dry-run the land price for a tier and a room configuration. */
export const FixedDepartureRoomQuotePreviewRequestSchema = z.object({
  categoryId: z.string().uuid(),
  roomGroups: z.array(RoomGroupInputSchema).min(1).max(20),
  /** When given, the date's per-person surcharge is included (as on create). */
  travelStartDate: z.iso.date().optional(),
});
export type FixedDepartureRoomQuotePreviewRequest = z.infer<
  typeof FixedDepartureRoomQuotePreviewRequestSchema
>;

/**
 * Which flight configuration the itinerary uses (config panel radio).
 *   - `LIVE`  : agent searches TBO fares by hand on the day plan (flightLegs).
 *   - `BLOCK` : held-inventory flights attached from `fdBlockFlights`.
 */
export const FixedDepartureItineraryFlightModeSchema = z.enum(['LIVE', 'BLOCK']);
export type FixedDepartureItineraryFlightMode = z.infer<
  typeof FixedDepartureItineraryFlightModeSchema
>;

/**
 * One block flight attached to a FD itinerary (BLOCK mode). Snapshotted onto the
 * itinerary so it survives a re-seed. `fdFlightId` records the FD master flight
 * it was picked from (null when the agent typed it in by hand); the rest is the
 * same shape as a master block flight. Airline + flight number are optional and
 * simply don't render on the day when absent. Per-pax fares default to zero.
 */
export const FdBlockFlightInputSchema = z.object({
  fdFlightId: z.string().uuid().nullable().optional(),
  fromCityId: z.string().uuid(),
  toCityId: z.string().uuid(),
  airlineId: z.string().uuid().nullable().optional(),
  flightNumber: z.string().max(20).nullable().optional(),
  departureDate: z.iso.date().nullable().optional(),
  departureTime: timeString.optional(),
  arrivalDate: z.iso.date().nullable().optional(),
  arrivalTime: timeString.optional(),
  adultPricePaise: paiseString.default('0'),
  childPricePaise: paiseString.default('0'),
  infantPricePaise: paiseString.default('0'),
});
export type FdBlockFlightInput = z.infer<typeof FdBlockFlightInputSchema>;

/**
 * Re-seeding an existing fixed-departure itinerary (ADR-0069 D13).
 *
 * While the itinerary is IN_PROGRESS the agent may change the tier, the travel
 * dates or the party, and everything downstream must follow: hotels re-seed
 * from the new tier, days re-date, the land price re-quotes off the grid and
 * the totals recompute. Publishing freezes it; changing it afterwards means a
 * new version, exactly as a FIT itinerary does.
 */
export const ReseedFixedDepartureItineraryRequestSchema = z.object({
  /**
   * Switch this itinerary onto a DIFFERENT template. Omit to re-seed from the
   * one it already uses. When set, `categoryId` must be a tier on the new
   * template - the whole trip is rebuilt from it.
   */
  fixedDepartureId: z.string().uuid().optional(),
  categoryId: z.string().uuid(),
  travelStartDate: z.iso.date(),
  adults: z.number().int().min(1).max(99),
  childrenWithBed: z.number().int().min(0).max(99).optional(),
  childrenWithoutBed: z.number().int().min(0).max(99).optional(),
  infants: z.number().int().min(0).max(99).optional(),
  singleRooms: z.number().int().min(0).max(99).optional(),
  /** Families > rooms; when present, the land price is computed per room. */
  roomGroups: z.array(RoomGroupInputSchema).min(1).max(20).optional(),
  /** Legacy: supplement ids at quantity 1. Prefer `vehicleSupplements`. */
  vehicleSupplementIds: z.array(z.string().uuid()).max(20).optional(),
  /**
   * Selected supplements with a quantity. PRIVATE supplements bill per-vehicle
   * x quantity; SIC supplements bill per-pax (quantity is ignored, kept 1).
   */
  vehicleSupplements: z
    .array(
      z.object({
        supplementId: z.string().uuid(),
        quantity: z.number().int().min(1).max(99),
      }),
    )
    .max(20)
    .optional(),
  /**
   * Flight mode for the itinerary. LIVE -> agent searches TBO fares from
   * `flightLegs`; BLOCK -> `fdBlockFlights` are attached per pax. Omit to leave
   * the current mode untouched.
   */
  flightMode: FixedDepartureItineraryFlightModeSchema.optional(),
  /**
   * The chosen block flights (BLOCK mode). Replaces the whole selection. Picked
   * from the FD master and/or typed in by hand; each renders on the day matching
   * its departure date. Ignored in LIVE mode.
   */
  fdBlockFlights: z.array(FdBlockFlightInputSchema).max(10).optional(),
  /**
   * @deprecated Superseded by `flightMode` + `fdBlockFlights`. Kept so older
   * callers don't break: `true` is treated as BLOCK with the FD's own flights.
   */
  seedBlockFlights: z.boolean().optional(),
  /** Visa (international) and insurance, reusing the FIT itinerary fields. */
  visaId: z.string().uuid().nullable().optional(),
  insuranceWanted: z.boolean().optional(),
  insuranceId: z.string().uuid().nullable().optional(),
  /**
   * From -> to flight legs to attach to the itinerary. Flights are the only
   * thing an agent configures by hand on a fixed departure; the actual fares
   * are then searched (live TBO) or picked (block) in the day plan, exactly as
   * on a normal itinerary. Omit to leave the current legs untouched.
   */
  flightLegs: z
    .array(
      z.object({
        fromCityId: z.string().uuid(),
        toCityId: z.string().uuid(),
        departureDate: z.iso.date().nullable().optional(),
      }),
    )
    .max(10)
    .optional(),
});
export type ReseedFixedDepartureItineraryRequest = z.infer<
  typeof ReseedFixedDepartureItineraryRequestSchema
>;

// ---------------------------------------------------------------------------
// Departure dates (optional; empty = free-date, like Kashmir)
// ---------------------------------------------------------------------------

export const CreateFixedDepartureDepartureDateRequestSchema = z.object({
  departureDate: z.iso.date(),
  surchargePaise: z
    .string()
    .regex(/^-?\d+$/)
    .optional(),
  isActive: z.boolean().optional(),
});
export type CreateFixedDepartureDepartureDateRequest = z.infer<
  typeof CreateFixedDepartureDepartureDateRequestSchema
>;

export const UpdateFixedDepartureDepartureDateRequestSchema =
  CreateFixedDepartureDepartureDateRequestSchema.partial();
export type UpdateFixedDepartureDepartureDateRequest = z.infer<
  typeof UpdateFixedDepartureDepartureDateRequestSchema
>;
